import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// The signed-in user's own LLM provider/model. The key itself is stored encrypted on the
// server and never sent back to the browser — we only get `has_key` and a short hint.
const LEGACY_KEY = "rulesqa.llm";
const CHANGED = "llm-settings-changed";
const OPEN = "open-llm-settings";

let current = null;
let loading = null;

const publish = (s) => { current = s; window.dispatchEvent(new Event(CHANGED)); return s; };

// Older versions kept the key in localStorage: move it to the account once, then forget it.
async function migrateLegacy() {
  const raw = localStorage.getItem(LEGACY_KEY);
  if (!raw) return null;
  localStorage.removeItem(LEGACY_KEY);
  try {
    const s = JSON.parse(raw);
    if (s?.provider && s?.model && s?.apiKey) return await api.saveMyLlm({ provider: s.provider, model: s.model, api_key: s.apiKey });
  } catch (e) {
    console.warn("Could not move the saved LLM key to your account", e);
  }
  return null;
}

export function loadLlmSettings() {
  if (!loading) {
    loading = (async () => publish((await migrateLegacy()) || (await api.getMyLlm())))()
      .catch((e) => { console.warn("Could not load LLM settings", e); return null; })
      .finally(() => { loading = null; });
  }
  return loading;
}

export const getLlmSettings = () => current;
export const saveLlmSettings = async (settings) => publish(await api.saveMyLlm(settings));
export const clearLlmSettings = async () => publish(await api.clearMyLlm());
export const resetLlmSettings = () => publish(null);

export const openLlmSettings = () => window.dispatchEvent(new Event(OPEN));

export function useOnOpenLlmSettings(handler) {
  useEffect(() => {
    window.addEventListener(OPEN, handler);
    return () => window.removeEventListener(OPEN, handler);
  }, [handler]);
}

export function useLlmSettings() {
  const [settings, setSettings] = useState(current);
  useEffect(() => {
    const sync = () => setSettings(current);
    window.addEventListener(CHANGED, sync);
    if (!current) loadLlmSettings();
    return () => window.removeEventListener(CHANGED, sync);
  }, []);
  return settings;
}
